/**
 * rtProtocolGateCore.mjs — субмиллиметровый шлюз качества МРТ (ядро, без браузера).
 *
 * Принимает теги серии, считает НАСТОЯЩИЙ воксель (по матрице СЪЁМКИ, а не рекона)
 * и выносит вердикт: прошла серия субмиллиметровый порог или нет. Ловит главный
 * обман — «рекон 1024 из съёмки 256» (интерполяция вместо реального разрешения).
 *
 * evaluateSubMm(tags, opts) → { level:'ok'|'warn'|'fail', trueInPlaneMm, throughMm,
 *   fovMm:[..], acqMatrix:[..], reconMatrix:[..], mrType, interpolated, reasons:[] }
 */

export function evaluateSubMm(tags, opts = {}) {
  const thr = opts.thresholdMm || 1.0;           // порог «субмиллиметр»
  const eps = 0.05;                              // допуск на округление
  const reasons = [];

  const through = num(tags.sliceThickness);
  const rows = num(tags.rows), cols = num(tags.columns);
  const ps = Array.isArray(tags.pixelSpacing) ? tags.pixelSpacing.map(num) : [num(tags.pixelSpacing), num(tags.pixelSpacing)];

  // FOV: ReconstructionDiameter, иначе rows*pixelSpacing
  let fovRow = num(tags.reconstructionDiameter) || (rows && ps[0] ? rows * ps[0] : 0);
  let fovCol = num(tags.reconstructionDiameter) || (cols && ps[1] ? cols * ps[1] : 0);

  // матрица съёмки (0018,1310): [freqRows, freqCols, phaseRows, phaseCols]
  const am = (tags.acquisitionMatrix || []).map(num);
  const acqFreq = Math.max(am[0] || 0, am[1] || 0);
  const acqPhase = Math.max(am[2] || 0, am[3] || 0);
  const acqRow = acqFreq || acqPhase;            // вдоль строк
  const acqCol = acqPhase || acqFreq;            // вдоль столбцов

  let trueInPlane, usedAcq = true;
  if (acqRow && acqCol && fovRow && fovCol) {
    // коэрцитивно берём КРУПНЕЙШИЙ (худший) воксель в плоскости
    trueInPlane = Math.max(fovRow / acqRow, fovCol / acqCol);
  } else {
    // матрицы съёмки нет — падаем на рекон (может быть интерполирован!)
    trueInPlane = Math.max(ps[0] || 0, ps[1] || 0);
    usedAcq = false;
    reasons.push('Матрица съёмки (AcquisitionMatrix) не указана — показано разрешение рекона, оно может быть интерполировано.');
  }

  // интерполяция: рекон существенно крупнее съёмки
  const acqMax = Math.max(acqRow, acqCol);
  const reconMax = Math.max(rows, cols);
  const interpolated = usedAcq && acqMax > 0 && reconMax > acqMax * 1.3;
  if (interpolated) {
    reasons.push(`Рекон ${reconMax}×${reconMax} из съёмки ${acqRow}×${acqCol} — «высокое разрешение» интерполировано, реальный воксель ${trueInPlane.toFixed(2)} мм.`);
  }

  const mrType = String(tags.mrAcquisitionType || '').toUpperCase();
  const is3D = mrType === '3D';

  // ── градуированный вердикт (зелёный / жёлтый / красный) ──
  const ideal = opts.idealMm || thr;                 // строгий порог (по умолчанию 1.0)
  const sliceRed = opts.sliceRedMm || 2.0;           // срез толще — тревога
  const planeRed = opts.planeRedMm || 1.5;           // воксель в плоскости крупнее — тревога

  const sliceOver = through && through > ideal + eps;
  const planeOver = trueInPlane && trueInPlane > ideal + eps;
  const sliceBad = through && through > sliceRed + eps;
  const planeBad = trueInPlane && trueInPlane > planeRed + eps;

  // [7.0.14] Две поправки к градуированному вердикту.
  //
  //  1) opts.expect3D перестал учитываться при переходе на градацию: параметр
  //     принимался, но на результат не влиял вовсе — expect3D:false и
  //     expect3D:true давали одинаковый ответ. Молча игнорируемая настройка
  //     хуже отсутствующей: она выглядит работающей.
  //
  //  2) Отсутствие тега MRAcquisitionType считалось за «всё в порядке»:
  //     not3D = mrType && !is3D, а при пустом теге mrType — пустая строка, то
  //     есть ложь, и предупреждение не выставлялось. При этом строка вердикта
  //     жёстко утверждала «съёмка 3D». В печатном заключении выходило прямое
  //     противоречие: в таблице «Тип съёмки: —», в обосновании «съёмка 3D».
  //     Ровно тот же изъян, что мы уже чинили: шлюз ручается за непроверенное.
  const want3D = opts.expect3D !== false;            // для СРХ по умолчанию ждём 3D
  const not3D = want3D && !!mrType && !is3D;         // объявлена, но не 3D
  const type3DUnknown = want3D && !mrType;           // тег не указан вовсе

  let mismatch = false;
  if (opts.expectName) {
    const desc = `${tags.seriesDescription || ''} ${tags.protocolName || ''}`.toLowerCase();
    mismatch = !opts.expectName.toLowerCase().split(/[\s,]+/).some(w => w && desc.includes(w));
  }

  // [7.0.13] Защита от пустых тегов (находка Андрея): отсутствующий тег num()
  // превращает в 0, и при нуле НИ одна проверка не срабатывала — серия без тегов
  // получала «ok». Для шлюза это худший отказ. Нет ключевого тега → сразу fail.
  const unknown = [];
  if (!through) unknown.push('толщину среза (SliceThickness)');
  if (!trueInPlane) unknown.push('размер пикселя в плоскости (PixelSpacing/AcquisitionMatrix)');

  let level;
  if (unknown.length) level = 'fail';
  else if (sliceBad || planeBad || (not3D && through && through > 1.5 + eps)) level = 'fail';
  else if (sliceOver || planeOver || not3D || type3DUnknown || interpolated || mismatch) level = 'warn';
  else level = 'ok';

  if (unknown.length) {
    // [7.0.18] Главную причину — ВПЕРЁД (замечание Андрея): при пустых тегах
    // раньше первой шла заметка про AcquisitionMatrix, а не суть отказа. unshift
    // ставит «не удалось прочитать…» во главу списка и в баннере, и в печати.
    reasons.unshift(`Не удалось прочитать ${unknown.join(' и ')}. Подтвердить субмиллиметр невозможно: данных для проверки нет.`);
  } else if (level === 'ok') {
    // Про 3D говорим ТОЛЬКО если тег это подтверждает — иначе заключение
    // утверждало бы то, чего в файле нет.
    reasons.unshift(`Субмиллиметр подтверждён: срез ${round2(through)} мм и воксель в плоскости ${round2(trueInPlane)} мм ≤ ${ideal} мм${is3D ? ', съёмка 3D' : ''}.`);
  } else if (level === 'fail') {
    if (sliceBad) reasons.push(`Толщина среза ${round2(through)} мм — грубо (тревога при > ${sliceRed} мм).`);
    if (planeBad) reasons.push(`Реальный воксель в плоскости ${round2(trueInPlane)} мм — грубо (тревога при > ${planeRed} мм).`);
    if (not3D && through > 1.5) reasons.push(`2D-съёмка с толстым срезом ${round2(through)} мм — не для точного планирования.`);
  } else {
    if (sliceOver && !sliceBad) reasons.push(`Толщина среза ${round2(through)} мм — чуть выше строгого субмиллиметра (${ideal} мм); для 3D-серии обычно приемлемо.`);
    if (planeOver && !planeBad) reasons.push(`Воксель в плоскости ${round2(trueInPlane)} мм — чуть выше ${ideal} мм.`);
    if (not3D) reasons.push(`Тип съёмки ${mrType} — не 3D; изотропный субмиллиметр не гарантирован.`);
    if (type3DUnknown) reasons.push('Тип съёмки (MRAcquisitionType) в файле не указан — подтвердить, что серия объёмная (3D), нельзя.');
  }
  if (mismatch) reasons.push(`Серия «${tags.seriesDescription || '—'}» не похожа на заказанную «${opts.expectName}».`);

  const isoSpread = (through && trueInPlane) ? Math.max(through, trueInPlane) / Math.min(through, trueInPlane) : 1;

  return {
    level, thresholdMm: thr,
    trueInPlaneMm: round2(trueInPlane), throughMm: round2(through),
    fovMm: [round2(fovRow), round2(fovCol)],
    acqMatrix: [acqRow, acqCol], reconMatrix: [rows, cols],
    mrType: mrType || '—', is3D, interpolated, isotropic: isoSpread <= 1.35,
    sequence: tags.seriesDescription || tags.protocolName || '—',
    modality: tags.modality || '—',
    reasons,
  };
}

function num(v) { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; }
function round2(v) { return Math.round((v || 0) * 100) / 100; }

// ═════════════════════════════════════════════════════════════════════════════
// [7.0.23] Печатное заключение — HTML вынесен сюда из rtProtocolGateModule.
// Причина: содержание документа (вердикт, цифры, обоснование, экранирование,
// авторство) — проверяемая логика; модулю остаётся только окно печати.
// ═════════════════════════════════════════════════════════════════════════════

function esc(s) { return String(s).replace(/[&<>"]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m])); }

/**
 * Полный HTML печатного заключения о контроле качества МР-исследования.
 * @param {object} res результат evaluateSubMm
 * @param {object} tags теги серии (как в _readTags модуля)
 * @returns {string}
 */
export function buildQualityReportHTML(res, tags) {
  const verdict = res.level === 'fail'
    ? { t: 'НЕ СООТВЕТСТВУЕТ субмиллиметровому требованию', c: '#c0161c' }
    : res.level === 'warn'
      ? { t: 'СООТВЕТСТВУЕТ ЧАСТИЧНО — есть замечания', c: '#b26a00' }
      : { t: 'СООТВЕТСТВУЕТ субмиллиметровому требованию', c: '#127a2e' };
  const reconVox = (tags.pixelSpacing && tags.pixelSpacing[0]) ? Number(tags.pixelSpacing[0]).toFixed(2) : '—';
  const rows = res.reasons.map(r => `<li>${esc(r)}</li>`).join('');
  const row = (k, v) => `<tr><td style="padding:4px 10px;border:1px solid #ccc;background:#f6f6f6">${k}</td><td style="padding:4px 10px;border:1px solid #ccc">${v}</td></tr>`;
  return `<!doctype html><html lang="ru"><head><meta charset="utf-8">
    <title>Заключение о контроле качества МРТ</title></head>
    <body style="font:13px/1.6 Georgia,serif;color:#111;max-width:720px;margin:24px auto;padding:0 16px">
      <h2 style="margin:0 0 2px">Заключение о контроле качества МР-исследования</h2>
      <div style="color:#666;margin-bottom:12px">Субмиллиметровый шлюз · Mikhmel Cornerstone4D DICOM Viewer Pro</div>
      <table style="border-collapse:collapse;width:100%;margin-bottom:12px">
        ${row('Пациент', esc(tags.patientName || '—') + (tags.patientId ? ' · ID ' + esc(tags.patientId) : ''))}
        ${row('Исследование', esc(tags.studyDesc || '—') + (tags.studyDate ? ' · ' + esc(tags.studyDate) : ''))}
        ${row('Серия', esc(tags.seriesDescription || tags.protocolName || '—') + ' · ' + esc(res.modality))}
      </table>
      <div style="font-size:16px;font-weight:800;color:${verdict.c};border:2px solid ${verdict.c};border-radius:8px;padding:10px 12px;margin-bottom:12px">
        ${verdict.t}
      </div>
      <table style="border-collapse:collapse;width:100%;margin-bottom:12px">
        ${row('Порог (субмиллиметр)', res.thresholdMm.toFixed(1) + ' мм')}
        ${row('Толщина среза', '<b>' + res.throughMm + ' мм</b>')}
        ${row('Реальный воксель в плоскости (по съёмке)', '<b>' + res.trueInPlaneMm + ' мм</b>')}
        ${row('Воксель по реконструкции', reconVox + ' мм' + (res.interpolated ? ' <span style="color:#c0161c">(интерполяция)</span>' : ''))}
        ${row('Матрица съёмки', res.acqMatrix[0] + ' × ' + res.acqMatrix[1])}
        ${row('Матрица реконструкции', res.reconMatrix[0] + ' × ' + res.reconMatrix[1])}
        ${row('Тип съёмки', res.mrType)}
        ${row('FOV', res.fovMm[0] + ' × ' + res.fovMm[1] + ' мм')}
      </table>
      <b>Обоснование:</b>
      <ul>${rows}</ul>
      <p style="color:#444;font-size:12px">Примечание: реальное разрешение определяется матрицей <b>съёмки</b> (AcquisitionMatrix),
      а не реконструкции. Увеличенная матрица реконструкции («рекон 1024») не добавляет диагностической
      детали, если съёмка выполнена крупной матрицей.</p>
      <div style="margin-top:28px;display:flex;justify-content:space-between;color:#333">
        <div>Дата: ____________________</div>
        <div>Врач: ____________________ / подпись</div>
      </div>
      <div style="margin-top:22px;padding-top:10px;border-top:1px solid #ddd;color:#777;font-size:11px">
        Mikhmel Cornerstone4D DICOM Viewer Pro · Автор — Михмель Андрей Вячеславович, врач-кардиолог · © babab.ru
      </div>
    </body></html>`;
}
